import React from 'react';

import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
} from 'recharts';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';

import getChartColors from './chartColors';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const filtered = payload.filter((item) => item.value > 0);

  const total = filtered.reduce((sum, item) => sum + item.value, 0);

  return (
    <Paper
      elevation={3}
      sx={(theme) => ({
        p: 2,
        minWidth: 180,
        backgroundColor: 'var(--color-surface-elevated)',
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      {filtered.map((item) => (
        <Box
          key={item.dataKey}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={0.5}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: item.color,
                borderRadius: '50%',
              }}
            />
            <Typography variant="body2">
              {item.dataKey}
            </Typography>
          </Box>

          <Typography variant="body2" fontWeight={500} ml={1}>
            {item.value}
          </Typography>
        </Box>
      ))}

      <Box
        mt={1}
        pt={1}
        borderTop="1px solid var(--color-border)"
        display="flex"
        justifyContent="space-between"
      >
        <Typography variant="body2" fontWeight={600}>
          Total
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {total}
        </Typography>
      </Box>
    </Paper>
  );
};

const StackedBarChart = ({ data, bars = [], stackId }) => {
  const theme = useTheme();
  const colors = getChartColors(theme, bars.length || 10);

  return (
    <ResponsiveContainer width="100%" height={350}>
      <BarChart data={data}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={theme.palette.divider} />

        <XAxis dataKey="month" tick={{ fill: theme.palette.text.secondary }} axisLine={{ stroke: theme.palette.divider }} />
        <YAxis tick={{ fill: theme.palette.text.secondary }} axisLine={{ stroke: theme.palette.divider }} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--color-hover-surface)' }} />

        {bars.map((name, index) => (
          <Bar
            key={name}
            dataKey={name}
            stackId={stackId}
            fill={colors[index % colors.length]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default StackedBarChart;
